// @flow

import React, { useState } from "react"
import styled from "styled-components"
import { useIntl } from "react-intl"
import { Alert, Steps, Spin, Form, Input, Tooltip, Icon } from "antd"
import { get } from "lodash"
import { PrimaryButton } from "@cabonline/shared/components"
import DriverWebStore from "shared/stores/DriverWebStore"
import UserStore from "../../../shared/stores/UserStore"
import EmailFormItem from "shared/components/form-items/EmailFormItem"
import {
  ParagraphPrimary,
  TitlePrimary,
  Link
} from "../../../shared/components/text"
import { removeWhitespaces } from "shared/utils/stringUtils"
import analytics from "shared/utils/services/analytics"
import { ACTIONS } from "shared/constants/gaEvents"

const ACCOUNT_STEPS = {
  VERIFY_PHONE: 0,
  CREATE_PROFILE: 1,
  CHOOSE_PASSWORD: 2
}

const ErrorAlert = styled(Alert)`
  margin: 12px 0;
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  background: white;
  padding: 12px;
  padding-top: 35px;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 340px;
`

const TitleWrapper = styled.div`
  padding-bottom: 24px;
`
const { Step } = Steps
const FormStep = styled(Step)`
  .ant-steps-item-description {
    padding-top: 12px;
  }
  .ant-steps-item-title {
    font-size: 18px;
  }
  &.ant-steps-item-finish {
    .ant-steps-item-title {
      font-size: 16px !important;
      font-style: italic;
      color: #bbbbbb;
    }
  }
`

const CreatingUserContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  padding: 12px;
  background: #000;
  color: white;
  justify-content: center;
  align-items: center;
`

const Loading: any = styled(Spin)`
  width: 120px;
  height: 120px;
  display: flex;
  margin-bottom: 60px;
`

const CreateAccountStep = styled.div`
  display: flex;
  flex-direction: column;
`

const InputWrapper = styled.div`
  max-width: ${p => p.width}px;
`

const DescriptionWrapper = styled.div`
  padding-bottom: 20px;
`
const EmailWrapper = styled.div`
  padding-bottom: 12px;
`

type Props = {
  match: Object,
  history: Object,
  form: Object
}

const DriverWebOnBoarding = ({ match, history, form }: Props) => {
  const Intl = useIntl()
  const { token } = match ? match.params : {}
  const { getFieldDecorator, getFieldValue, getFieldError } = form
  // States
  const [activeStep, setActiveStep] = useState(0)
  const [driverEmail, setDriverEmail] = useState("")
  const [tflError, setTflError] = useState()
  const [emailError, setEmailError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [emailExists, setEmailExists] = useState(false)
  const [creatingAccount, setCreatingAccount] = useState(false)

  const verifyPhoneNumber = async () => {
    analytics.logInteraction(
      ACTIONS.DRIVER_CLICK_CONTINUE,
      "Driver clicked continue in verify phonenumber step"
    )
    const tfl = getFieldValue("tfl")
    if (!tfl) {
      setTflError(
        Intl.formatMessage({ id: "apps.driver-web.onboarding.error.enterTfl" })
      )
      return
    }
    try {
      await DriverWebStore.verifyPhoneNumber(token, tfl)
      setTflError(null)
      setActiveStep(ACCOUNT_STEPS.CREATE_PROFILE)
    } catch (e) {
      if (e.response && e.response.data && e.response.data.errors[0]) {
        const attemptsLeft = get(
          e,
          "response.data.errors[0].params.remainingAttempts",
          0
        )

        if (attemptsLeft) {
          setTflError(
            Intl.formatMessage(
              { id: "apps.driver-web.onboarding.error.wrongTfl.attemptsLeft" },
              { attemptsLeft }
            )
          )
        } else {
          setTflError(
            Intl.formatMessage({
              id: "apps.driver-web.onboarding.error.wrongTfl.expired"
            })
          )
        }
      }
    }
  }

  const verifyEmail = async () => {
    analytics.logInteraction(
      ACTIONS.DRIVER_CLICK_CONTINUE,
      "Driver clicked continue in create profile step"
    )
    const email = getFieldValue("email")
    setDriverEmail(email)
    const error = getFieldError("email")
    if (error) return
    if (!email) {
      setEmailError(
        Intl.formatMessage({
          id: "apps.driver-web.onboarding.error.enterEmail"
        })
      )
      return
    }

    try {
      const user = await DriverWebStore.addDriverToSystemUser(token, email)
      if (user.data && user.data.id) {
        setEmailExists(true)
        setActiveStep(ACCOUNT_STEPS.CHOOSE_PASSWORD)
      }
    } catch (e) {
      if (
        e.response &&
        e.response.data.errors &&
        e.response.data.errors[0].code === "NOT_FOUND"
      ) {
        setEmailError("")
        setActiveStep(ACCOUNT_STEPS.CHOOSE_PASSWORD)
        return
      }

      if (
        e.response &&
        e.response.data.errors &&
        e.response.data.errors[0].code ===
          "USER_ALREADY_CONNECTED_TO_DIFFERENT_DRIVER"
      ) {
        setEmailError(e.response.data.errors[0].message)
        return
      }
      // This an unknown error! we don't want to move forward!
      setEmailError(
        Intl.formatMessage({ id: "apps.driver-web.onboarding.error.unknown" })
      )
    }
  }

  const createAccount = async () => {
    analytics.logInteraction(
      ACTIONS.DRIVER_CLICK_SAVE_ACCOUNT,
      "Driver clicked on save after entering his password"
    )
    const password = getFieldValue("password")
    if (!password) {
      setPasswordError(
        Intl.formatMessage({
          id: "apps.driver-web.onboarding.error.enterPassword"
        })
      )
      return
    }
    try {
      setCreatingAccount(true)
      const newUser = await DriverWebStore.createSystemUser(
        token,
        driverEmail,
        password
      )
      if (
        newUser &&
        newUser.data &&
        newUser.data.id &&
        newUser.data.emailAddress === driverEmail.toLowerCase()
      ) {
        await UserStore.login(driverEmail, password)
        return history.push("/dashboard/driver-web/profile")
      } else {
        setCreatingAccount(false)
        setPasswordError(
          Intl.formatMessage({
            id: "apps.driver-web.onboarding.error.unableCreateAccount"
          })
        )
      }
    } catch (error) {
      setCreatingAccount(false)
    }
  }

  const renderVerifyPhoneStep = () => {
    if (activeStep !== ACCOUNT_STEPS.VERIFY_PHONE) return
    return (
      <CreateAccountStep>
        <DescriptionWrapper>
          <ParagraphPrimary>
            {Intl.formatMessage({
              id: "apps.driver-web.onboarding.tfl.description"
            })}
          </ParagraphPrimary>
        </DescriptionWrapper>

        <InputWrapper width={270}>
          <Form.Item
            label={
              <span>
                {Intl.formatMessage({
                  id: "apps.driver-web.onboarding.tfl.label"
                })}
                &nbsp;
                <Tooltip
                  title={Intl.formatMessage({
                    id: "apps.driver-web.onboarding.tfl.tooltip"
                  })}
                >
                  <Icon type="question-circle-o" />
                </Tooltip>
              </span>
            }
          >
            {getFieldDecorator("tfl")(
              <Input
                placeholder={Intl.formatMessage({
                  id: "apps.driver-web.onboarding.tfl.placeholder"
                })}
              />
            )}
          </Form.Item>
        </InputWrapper>

        {tflError && <ErrorAlert description={tflError} type="error" />}

        <PrimaryButton onClick={verifyPhoneNumber}>
          {Intl.formatMessage({ id: "common.continue" })}
        </PrimaryButton>
      </CreateAccountStep>
    )
  }

  const renderCreateProfileStep = () => {
    if (activeStep !== ACCOUNT_STEPS.CREATE_PROFILE) return
    return (
      <CreateAccountStep>
        <DescriptionWrapper>
          <ParagraphPrimary>
            {Intl.formatMessage({
              id: "apps.driver-web.onboarding.email.description"
            })}
          </ParagraphPrimary>
        </DescriptionWrapper>
        <InputWrapper width={270}>
          <EmailFormItem
            getFieldDecorator={getFieldDecorator}
            name="email"
            normalize={removeWhitespaces}
            label={Intl.formatMessage({
              id: "apps.driver-web.onboarding.email.label"
            })}
            placeholder={Intl.formatMessage({ id: "common.email" })}
          />
        </InputWrapper>

        {emailError.length > 0 && (
          <ErrorAlert description={emailError} type="error" />
        )}

        <PrimaryButton onClick={verifyEmail}>
          {Intl.formatMessage({ id: "common.continue" })}
        </PrimaryButton>
      </CreateAccountStep>
    )
  }

  const renderChoosePasswordStep = () => {
    if (activeStep !== ACCOUNT_STEPS.CHOOSE_PASSWORD) return
    if (!emailExists) {
      return (
        <CreateAccountStep>
          <InputWrapper width={270}>
            <Form.Item
              label={Intl.formatMessage({
                id: "apps.driver-web.onboarding.password.label"
              })}
            >
              {getFieldDecorator("password", {
                rules: [
                  {
                    message: Intl.formatMessage({
                      id: "apps.driver-web.onboarding.password.error.length"
                    }),
                    min: 8,
                    max: 100
                  },
                  {
                    validator: (rule, value, callback) => {
                      const regex = /[^\na-zA-Z0-9]+/g
                      const isInvalid = regex.test(value)
                      if (isInvalid) {
                        callback(
                          Intl.formatMessage({
                            id:
                              "apps.driver-web.onboarding.password.error.validation"
                          })
                        )
                      } else {
                        callback()
                      }
                    }
                  }
                ]
              })(<Input.Password />)}
            </Form.Item>
          </InputWrapper>
          <ParagraphPrimary>
            {Intl.formatMessage({
              id: "apps.driver-web.onboarding.password.validation"
            })}
          </ParagraphPrimary>

          {passwordError.length > 0 && (
            <ErrorAlert description={passwordError} type="error" />
          )}

          <PrimaryButton onClick={createAccount}>
            {Intl.formatMessage({ id: "common.save" })}
          </PrimaryButton>
        </CreateAccountStep>
      )
    } else {
      return (
        <CreateAccountStep>
          <div>
            <EmailWrapper>{driverEmail}</EmailWrapper>
            <ParagraphPrimary>
              {Intl.formatMessage({
                id:
                  "apps.driver-web.onboarding.password.existingAccount.description"
              })}
            </ParagraphPrimary>
          </div>
          <Link
            to={{
              pathname: "/",
              state: {
                email: driverEmail
              }
            }}
          >
            <PrimaryButton>
              {Intl.formatMessage({
                id: "apps.driver-web.onboarding.continuetologin"
              })}
            </PrimaryButton>
          </Link>
        </CreateAccountStep>
      )
    }
  }

  const handleFormSubmit = e => {
    e.preventDefault()
  }

  if (creatingAccount) {
    return (
      <CreatingUserContainer>
        <Loading
          indicator={
            <Icon
              type="loading"
              style={{ fontSize: 120, color: "#ffcc00" }}
              spin
            />
          }
        />
        <ParagraphPrimary white>
          {Intl.formatMessage({
            id: "apps.driver-web.onboarding.creatingAccount"
          })}
        </ParagraphPrimary>
      </CreatingUserContainer>
    )
  } else {
    return (
      <Container>
        <ContentWrapper>
          <TitleWrapper>
            <TitlePrimary>
              {Intl.formatMessage({ id: "apps.driver-web.onboarding.title" })}
            </TitlePrimary>
          </TitleWrapper>
          <Form onSubmit={handleFormSubmit}>
            <Steps direction="vertical" current={activeStep}>
              <FormStep
                title={Intl.formatMessage({
                  id: "apps.driver-web.onboarding.tfl.title"
                })}
                description={renderVerifyPhoneStep()}
              />
              <FormStep
                title={Intl.formatMessage({
                  id: "apps.driver-web.onboarding.email.title"
                })}
                description={renderCreateProfileStep()}
              />
              <FormStep
                title={Intl.formatMessage({
                  id: "apps.driver-web.onboarding.password.title"
                })}
                description={renderChoosePasswordStep()}
              />
            </Steps>
          </Form>
        </ContentWrapper>
      </Container>
    )
  }
}

const WrappedForm: any = Form.create({ name: "createAccount" })(
  DriverWebOnBoarding
)
export default WrappedForm
